<template>
    <form-wrapper @submit="handleFormSubmission(data)">
        <form-row>
            <form-cell>
                <text-field
                    :label="$t('forms.auth.login.fields.email.label')"
                    :placeholder="$t('forms.auth.login.fields.email.placeholder')"
                    :hasAutofocus="!requiresIpVerification"
                    :isRequired="true"
                    :formData="data"
                    :error="errorMessage"
                    :isDisabled="isSending || isDisabled || requiresIpVerification"
                    valuePath="email"
                    type="email"
                    @change="changeEmail($event)"
                ></text-field>
            </form-cell>
        </form-row>

        <common-buttons align="center" class="resend-verification">
            <common-button
                v-if="notVerifed"
                :label="$t('forms.auth.login.microcopy.resend')"
                type="primary"
                :isSubmittable="false"
                @click="resendVerification()"
            ></common-button>
        </common-buttons>

        <template v-if="requiresIpVerification">
            <form-row>
                <form-cell>
                    <p class="ip-verification-message">
                        {{ $t('forms.auth.login.microcopy.ip_verification_sent') }}
                    </p>
                    <text-field
                        :label="$t('forms.auth.login.fields.verify_token.label')"
                        :placeholder="$t('forms.auth.login.fields.verify_token.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        :error="verifyTokenError"
                        :isDisabled="isSending || isDisabled"
                        :hasAutofocus="true"
                        valuePath="verifyToken"
                        :maxLength="6"
                        @change="changeVerifyToken($event)"
                    ></text-field>
                </form-cell>
            </form-row>
        </template>

        <form-row v-else-if="!notVerifed">
            <form-cell>
                <text-field
                    :label="$t('forms.auth.login.fields.password.label')"
                    :placeholder="$t('forms.auth.login.fields.password.placeholder')"
                    :isRequired="true"
                    :formData="data"
                    :isDisabled="isSending || isDisabled"
                    valuePath="password"
                    :type="passwordInputFieldType"
                    :maxLength="40"
                    @change="change($event)"
                >
                    <template #right>
                        <router-link :to="{ name: 'auth.forgot-password' }" tabindex="1">
                            {{ $t('forms.auth.login.microcopy.forgot_password') }}
                        </router-link>
                    </template>
                    <template #icon>
                        <common-toggle-visibility-button
                            @visibility-toggle-switch="showPassword"
                        ></common-toggle-visibility-button>
                    </template>
                </text-field>
            </form-cell>
        </form-row>

        <template #footer>
            <template v-if="requiresIpVerification">
                <common-buttons>
                    <common-button
                        :label="$t('forms.auth.login.microcopy.cancel')"
                        type="secondary"
                        :isSubmittable="false"
                        :isDisabled="isSending || isDisabled"
                        @click="cancelIpVerification()"
                    ></common-button>
                    <common-button
                        :isProcessing="isSending || isDisabled"
                        :label="$t('forms.auth.login.microcopy.verify')"
                        type="primary"
                    ></common-button>
                </common-buttons>
                <common-buttons align="center" class="resend-verification">
                    <common-button
                        :label="resendButtonLabel"
                        type="secondary"
                        :isSubmittable="false"
                        :isDisabled="isSending || isDisabled || !canResendCode"
                        @click="resendIpVerification()"
                    ></common-button>
                </common-buttons>
            </template>
            <common-buttons v-else-if="!notVerifed">
                <common-button
                    :isProcessing="isSending || isDisabled"
                    :label="$t('forms.auth.login.microcopy.login')"
                    type="primary"
                ></common-button>
            </common-buttons>
        </template>

        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>
    </form-wrapper>
</template>

<script>
    import dayjs from 'dayjs'
    import FormMixin from 'mixins/FormMixin'
    import GoogleAnalyticsService from 'services/GoogleAnalyticsService'
    import PasswordToggleVisibilityMixin from 'mixins/PasswordToggleVisibilityMixin'
    import AuthAPI from 'api/AuthAPI'
    import LogsTrackerAPI from 'api/LogsTrackerAPI'

    export default {
        mixins: [FormMixin, PasswordToggleVisibilityMixin],

        props: {
            isDisabled: { type: Boolean, default: false },
        },

        data() {
            return {
                errorMessage: null,
                notVerifed: false,
                notificationMessage: '',
                requiresIpVerification: false,
                verifyTokenError: null,
                canResendAt: null,
                pendingAuthTokens: null,
                nowTick: Date.now(),
                resendCooldownTimer: null,
            }
        },

        computed: {
            canResendCode() {
                if (!this.canResendAt) return true
                return dayjs(this.nowTick).isAfter(dayjs(this.canResendAt))
            },

            resendButtonLabel() {
                if (this.canResendCode) {
                    return this.$t('forms.auth.login.microcopy.resend_code')
                }

                const secondsLeft = Math.max(0, dayjs(this.canResendAt).diff(dayjs(this.nowTick), 'second'))
                return this.$t('forms.auth.login.microcopy.resend_code_wait', { seconds: secondsLeft })
            },
        },

        methods: {
            async getClientIpAddress() {
                return fetch('https://api.ipify.org?format=json')
                    .then((res) => res.json())
                    .then(({ ip }) => ip)
                    .catch(() => null)
            },

            logLoginAttempt({ email, status, error, verifyToken, resend }) {
                return this.getClientIpAddress().then((ipAddress) =>
                    LogsTrackerAPI.create({
                        email,
                        eventType: 'LOGIN',
                        status,
                        ipAddress,
                        userAgent: navigator.userAgent,
                        failureReason: error?.message,
                        verifyToken: verifyToken || undefined,
                        resend: resend || undefined,
                    }),
                )
            },

            completeLogin() {
                const { accessToken, idToken, refreshToken } = this.pendingAuthTokens
                this.$auth.logIn(accessToken, idToken, refreshToken)
                GoogleAnalyticsService.emitLogInGoogleAnalytics(this.$ga)
                this.clearIpVerificationState()
                this.emitEvent({ name: 'sent' })
                this.isSending = false
            },

            enterIpVerification({ canResendAt }) {
                this.requiresIpVerification = true
                this.verifyTokenError = null
                this.data.verifyToken = ''
                this.canResendAt = canResendAt || null
                this.startResendCooldownTimer()
                this.emitEvent({ name: 'form-submitted', payload: { areFieldsDisabled: false } })
                this.isSending = false
            },

            clearIpVerificationState() {
                this.requiresIpVerification = false
                this.verifyTokenError = null
                this.canResendAt = null
                this.pendingAuthTokens = null
                this.data.verifyToken = ''
                this.stopResendCooldownTimer()
            },

            startResendCooldownTimer() {
                this.stopResendCooldownTimer()
                this.nowTick = Date.now()
                this.resendCooldownTimer = setInterval(() => {
                    this.nowTick = Date.now()
                    if (this.canResendCode) {
                        this.stopResendCooldownTimer()
                    }
                }, 1000)
            },

            stopResendCooldownTimer() {
                if (this.resendCooldownTimer) {
                    clearInterval(this.resendCooldownTimer)
                    this.resendCooldownTimer = null
                }
            },

            handleIpVerificationResponse(response) {
                if (response?.requireValidation) {
                    this.enterIpVerification({ canResendAt: response.canResendAt })
                    return true
                }

                this.completeLogin()
                return false
            },

            getVerifyTokenFieldError(error) {
                const fieldError = error?.response?.data?.[0]
                if (fieldError?.field !== 'verifyToken') return null

                if (fieldError.code === 'entity.expired') {
                    return this.$t('forms.auth.login.microcopy.ip_verification_expired')
                }
                if (fieldError.code === 'entity.invalid') {
                    return this.$t('forms.auth.login.microcopy.ip_verification_invalid')
                }
                return fieldError.message
            },

            handleFormSubmission({ email, password, verifyToken }) {
                if (this.requiresIpVerification) {
                    return this.submitIpVerification({ email, verifyToken })
                }

                this.emitEvent({ name: 'form-submitted', payload: { areFieldsDisabled: true } })
                this.handleRequest(AuthAPI.logIn(email, password))
                    .then((AuthenticationResult) => {
                        const { accessToken, idToken, refreshToken } = AuthenticationResult.signInUserSession
                        this.pendingAuthTokens = {
                            accessToken: accessToken.jwtToken,
                            idToken: idToken.jwtToken,
                            refreshToken: refreshToken.token,
                        }

                        return this.logLoginAttempt({ email, status: 'SUCCESS' }).then((response) => {
                            this.handleIpVerificationResponse(response)
                        })
                    })
                    .catch((error) => {
                        this.emitEvent({ name: 'form-submitted', payload: { areFieldsDisabled: false } })

                        if (this.pendingAuthTokens) {
                            this.pendingAuthTokens = null
                            this.errorMessage = this.$t('forms.auth.login.microcopy.ip_verification_resend_failed')
                            this.data.password = ''
                            AuthAPI.logOut().catch(() => {})
                            return
                        }

                        if (error.message?.includes('PasswordExpired')) {
                            this.$router.push({ name: 'auth.forgot-password', query: { email, isPasswordExpired: true } })
                        }
                        if (error.code === 'UserNotConfirmedException') {
                            this.errorMessage = this.$t('forms.auth.login.microcopy.not_verified')
                            this.notVerifed = true
                        } else {
                            this.errorMessage = this.$t('forms.auth.login.microcopy.not_found_error')
                        }
                        this.data.password = ''
                        return this.logLoginAttempt({ email, status: 'FAILURE', error })
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },

            submitIpVerification({ email, verifyToken }) {
                const code = (verifyToken || '').trim().toUpperCase()
                this.verifyTokenError = null

                if (code.length !== 6) {
                    this.verifyTokenError = this.$t('forms.auth.login.microcopy.ip_verification_invalid')
                    return
                }

                this.emitEvent({ name: 'form-submitted', payload: { areFieldsDisabled: true } })
                this.handleRequest(this.logLoginAttempt({ email, status: 'SUCCESS', verifyToken: code }))
                    .then((response) => {
                        this.handleIpVerificationResponse(response)
                    })
                    .catch((error) => {
                        this.emitEvent({ name: 'form-submitted', payload: { areFieldsDisabled: false } })
                        this.verifyTokenError =
                            this.getVerifyTokenFieldError(error) || this.$t('forms.auth.login.microcopy.ip_verification_invalid')
                        this.data.verifyToken = ''
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },

            resendIpVerification() {
                if (!this.canResendCode) return

                this.verifyTokenError = null
                this.handleRequest(this.logLoginAttempt({ email: this.data.email, status: 'SUCCESS', resend: true }))
                    .then((response) => {
                        if (response?.requireValidation) {
                            this.canResendAt = response.canResendAt || null
                            this.startResendCooldownTimer()
                            this.notificationMessage = this.$t('forms.auth.login.microcopy.ip_verification_resent')
                            this.$refs.commonSuccessNotificationChild.showNotification()
                        }
                    })
                    .catch((error) => {
                        const fieldError = error?.response?.data?.[0]
                        this.verifyTokenError =
                            fieldError?.message || this.$t('forms.auth.login.microcopy.ip_verification_resend_failed')
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },

            cancelIpVerification() {
                this.clearIpVerificationState()
                this.data.password = ''
                this.emitEvent({ name: 'form-submitted', payload: { areFieldsDisabled: false } })
                AuthAPI.logOut().catch(() => {})
            },

            changeEmail(event) {
                this.change(event)
                this.errorMessage = ''
            },

            changeVerifyToken(event) {
                this.change(event)
                this.verifyTokenError = null
            },

            resendVerification() {
                this.handleRequest(AuthAPI.resendVerificationEmail(this.data.email))
                    .then(() => {
                        this.notVerifed = false
                        this.errorMessage = ''
                        this.notificationMessage = this.$t('forms.auth.login.microcopy.email_sent')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                    })
                    .catch(() => {
                        this.notVerifed = false
                        this.errorMessage = ''
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },
        },

        mounted() {
            if (this.$route.query.email) {
                this.data.email = this.$route.query.email
            }
        },

        beforeDestroy() {
            this.stopResendCooldownTimer()
        },
    }
</script>

<style scoped lang="scss">
    .resend-verification {
        margin-top: 20px;
    }

    .ip-verification-message {
        margin: 0 0 16px;
        color: var(--color-text-secondary, #667085);
        font-size: 14px;
        line-height: 1.4;
        text-align: center;
    }
</style>
